/* GENERATED FILE — DO NOT EDIT.
 *
 * The progress views a coach can tick per exercise, emitted from the enum the phone's chart menu compiles:
 *   HybridTrainer/Services/ProgressChartPlan.swift  (`Field`, `offered(trackingFields:)`)
 *
 * Regenerate:  KATA_EMIT_WEB_TOKENS=1 ./tools/engine-tests/run.sh
 * The harness gate `w72-3-the-exercise-builder` regenerates this in memory on every run and fails if it differs.
 *
 * `coach_exercises.progress_views` stores these KEYS; EMPTY means every view the exercise offers. Which views
 * an exercise offers is DERIVED from what it logs — `OFFERED` is the phone's own derivation, executed for every
 * combination of the tracking words that open a chart field. Any other logged field opens none.
 */

export type ProgressView = {
  key: ProgressViewKey;
  label: string;
  longLabel: string;
  unit: string;
};

export type ProgressViewKey = "oneRM" | "topSet" | "volume" | "bestSet" | "totalReps" | "longestHold" | "timeUnderTension" | "longestDistance" | "totalDistance" | "mostRounds";

export const PROGRESS_VIEWS: readonly ProgressView[] = [
  { key: "oneRM", label: "Est. 1RM", longLabel: "Estimated 1RM — worked out from the best set", unit: "kg" },
  { key: "topSet", label: "Top set", longLabel: "Top set — heaviest weight that day", unit: "kg" },
  { key: "volume", label: "Volume", longLabel: "Volume — total weight × reps", unit: "kg" },
  { key: "bestSet", label: "Best set", longLabel: "Best set — most reps in one set", unit: "reps" },
  { key: "totalReps", label: "Total reps", longLabel: "Total reps — every rep that day", unit: "reps" },
  { key: "longestHold", label: "Longest hold", longLabel: "Longest hold — best single hold", unit: "s" },
  { key: "timeUnderTension", label: "Time under tension", longLabel: "Time under tension — all holds added up", unit: "s" },
  { key: "longestDistance", label: "Longest distance", longLabel: "Longest distance — farthest single set", unit: "m" },
  { key: "totalDistance", label: "Total distance", longLabel: "Total distance — every set added up", unit: "m" },
  { key: "mostRounds", label: "Most rounds", longLabel: "Most rounds — best single set", unit: "rounds" },
];

/** W136 B2 S3r5 P2 — the views `coach_exercises.progress_views` may STORE (its CHECK); any other view is charted, never
 *  ticked, and never narrowed away by the ticks (`ProgressChartPlan.storedViews`). */
export const STORED_VIEWS: readonly ProgressViewKey[] = ["oneRM", "topSet", "volume", "bestSet", "totalReps", "longestHold", "timeUnderTension", "longestDistance", "totalDistance", "mostRounds"];

/** The tracking words that open a chart field, in the key's order. */
export const CHARTED_TRACKING_FIELDS: readonly string[] = ["reps", "weight", "time", "distance_m", "distance_km", "rounds"];

/** The views offered for each combination of charted words (joined with `+`, `""` for none). */
export const OFFERED: Readonly<Record<string, readonly ProgressViewKey[]>> = {
  "": [],
  "reps": ["bestSet", "totalReps"],
  "weight": ["topSet"],
  "reps+weight": ["oneRM", "topSet", "volume", "bestSet", "totalReps"],
  "time": ["longestHold", "timeUnderTension"],
  "reps+time": ["bestSet", "totalReps", "longestHold", "timeUnderTension"],
  "weight+time": ["topSet", "longestHold", "timeUnderTension"],
  "reps+weight+time": ["oneRM", "topSet", "volume", "bestSet", "totalReps", "longestHold", "timeUnderTension"],
  "distance_m": ["longestDistance", "totalDistance"],
  "reps+distance_m": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+distance_m": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+distance_m": ["oneRM", "topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "time+distance_m": ["longestDistance", "totalDistance"],
  "reps+time+distance_m": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+time+distance_m": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+time+distance_m": ["oneRM", "topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "distance_km": ["longestDistance", "totalDistance"],
  "reps+distance_km": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+distance_km": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+distance_km": ["oneRM", "topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "time+distance_km": ["longestDistance", "totalDistance"],
  "reps+time+distance_km": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+time+distance_km": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+time+distance_km": ["oneRM", "topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "distance_m+distance_km": ["longestDistance", "totalDistance"],
  "reps+distance_m+distance_km": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+distance_m+distance_km": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+distance_m+distance_km": ["oneRM", "topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "time+distance_m+distance_km": ["longestDistance", "totalDistance"],
  "reps+time+distance_m+distance_km": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+time+distance_m+distance_km": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+time+distance_m+distance_km": ["oneRM", "topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "rounds": ["mostRounds"],
  "reps+rounds": ["mostRounds"],
  "weight+rounds": ["topSet", "mostRounds"],
  "reps+weight+rounds": ["topSet", "mostRounds"],
  "time+rounds": ["mostRounds"],
  "reps+time+rounds": ["mostRounds"],
  "weight+time+rounds": ["topSet", "mostRounds"],
  "reps+weight+time+rounds": ["topSet", "mostRounds"],
  "distance_m+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+distance_m+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+distance_m+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+distance_m+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "time+distance_m+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+time+distance_m+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+time+distance_m+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+time+distance_m+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "time+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+time+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+time+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+time+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "distance_m+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+distance_m+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+distance_m+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+distance_m+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "time+distance_m+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+time+distance_m+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+time+distance_m+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+time+distance_m+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
};

/** The views an exercise logging these fields can offer, in the chart's fixed order. */
export function offeredViews(trackingFields: readonly string[]): readonly ProgressViewKey[] {
  const key = CHARTED_TRACKING_FIELDS.filter((w) => trackingFields.includes(w)).join("+");
  return OFFERED[key] ?? [];
}

/** W125 — the views a coach can TICK (`ProgressChartPlan.tickable`): the offered ones less `oneRM`, which is the
 *  exercise's own `1RM` switch (`one_rm_enabled`). */
export const TICKABLE: Readonly<Record<string, readonly ProgressViewKey[]>> = {
  "": [],
  "reps": ["bestSet", "totalReps"],
  "weight": ["topSet"],
  "reps+weight": ["topSet", "volume", "bestSet", "totalReps"],
  "time": ["longestHold", "timeUnderTension"],
  "reps+time": ["bestSet", "totalReps", "longestHold", "timeUnderTension"],
  "weight+time": ["topSet", "longestHold", "timeUnderTension"],
  "reps+weight+time": ["topSet", "volume", "bestSet", "totalReps", "longestHold", "timeUnderTension"],
  "distance_m": ["longestDistance", "totalDistance"],
  "reps+distance_m": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+distance_m": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+distance_m": ["topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "time+distance_m": ["longestDistance", "totalDistance"],
  "reps+time+distance_m": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+time+distance_m": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+time+distance_m": ["topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "distance_km": ["longestDistance", "totalDistance"],
  "reps+distance_km": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+distance_km": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+distance_km": ["topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "time+distance_km": ["longestDistance", "totalDistance"],
  "reps+time+distance_km": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+time+distance_km": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+time+distance_km": ["topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "distance_m+distance_km": ["longestDistance", "totalDistance"],
  "reps+distance_m+distance_km": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+distance_m+distance_km": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+distance_m+distance_km": ["topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "time+distance_m+distance_km": ["longestDistance", "totalDistance"],
  "reps+time+distance_m+distance_km": ["bestSet", "totalReps", "longestDistance", "totalDistance"],
  "weight+time+distance_m+distance_km": ["topSet", "longestDistance", "totalDistance"],
  "reps+weight+time+distance_m+distance_km": ["topSet", "volume", "bestSet", "totalReps", "longestDistance", "totalDistance"],
  "rounds": ["mostRounds"],
  "reps+rounds": ["mostRounds"],
  "weight+rounds": ["topSet", "mostRounds"],
  "reps+weight+rounds": ["topSet", "mostRounds"],
  "time+rounds": ["mostRounds"],
  "reps+time+rounds": ["mostRounds"],
  "weight+time+rounds": ["topSet", "mostRounds"],
  "reps+weight+time+rounds": ["topSet", "mostRounds"],
  "distance_m+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+distance_m+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+distance_m+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+distance_m+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "time+distance_m+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+time+distance_m+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+time+distance_m+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+time+distance_m+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "time+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+time+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+time+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+time+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "distance_m+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+distance_m+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+distance_m+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+distance_m+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "time+distance_m+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "reps+time+distance_m+distance_km+rounds": ["longestDistance", "totalDistance", "mostRounds"],
  "weight+time+distance_m+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
  "reps+weight+time+distance_m+distance_km+rounds": ["topSet", "longestDistance", "totalDistance", "mostRounds"],
};

export function tickableViews(trackingFields: readonly string[]): readonly ProgressViewKey[] {
  const key = CHARTED_TRACKING_FIELDS.filter((w) => trackingFields.includes(w)).join("+");
  return TICKABLE[key] ?? [];
}

/** W125 — the editor draws the `1RM` switch only for these combinations (`ProgressChartPlan.offersOneRMSwitch`). */
export const ONE_RM_SWITCH: readonly string[] = ["reps+weight", "reps+weight+time", "reps+weight+distance_m", "reps+weight+time+distance_m", "reps+weight+distance_km", "reps+weight+time+distance_km", "reps+weight+distance_m+distance_km", "reps+weight+time+distance_m+distance_km"];

export function offersOneRMSwitch(trackingFields: readonly string[]): boolean {
  return ONE_RM_SWITCH.includes(CHARTED_TRACKING_FIELDS.filter((w) => trackingFields.includes(w)).join("+"));
}
